import { useEffect } from "react";

const SITE_URL = "https://frisyre.com";
const SITE_NAME = "FRISYRE";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const TAGLINE = "Learn Hacking. Get Certified. Pay Nothing.";

export interface SeoProps {
  /** Page title — appended with " | FRISYRE" automatically unless `noBrandSuffix`. */
  title: string;
  /** ≤160 chars meta description. */
  description: string;
  /** Path-only canonical (e.g. "/track/foundations"). Defaults to current pathname. */
  path?: string;
  /** Absolute or root-relative image URL. */
  image?: string;
  /** og:type — "website" | "article" | "course". */
  type?: "website" | "article" | "course";
  /** Skip the " | FRISYRE" suffix when title already contains brand. */
  noBrandSuffix?: boolean;
  /** Block indexing for utility pages (auth, dashboard, etc.). */
  noindex?: boolean;
  /** Optional keywords (Google ignores; useful for Bing/DDG). */
  keywords?: string;
  /** JSON-LD object(s) injected as <script type="application/ld+json">. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/** Idempotently set or create a meta/link tag identified by name/property/rel. */
function setTag(
  selector: string,
  attrs: Record<string, string>,
  tagName: "meta" | "link" = "meta",
) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = document.createElement(tagName);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}

/** SEO/meta manager — drop one <Seo /> at the top of every page. */
export function Seo({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  type = "website",
  noBrandSuffix = false,
  noindex = false,
  keywords,
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    // Build the shortest title that still carries the brand and stays under 60 chars
    // for SERP display. Order: full → with brand → bare title.
    const withTagline = `${title} | ${SITE_NAME} — ${TAGLINE}`;
    const withBrand = `${title} | ${SITE_NAME}`;
    const fullTitle = noBrandSuffix
      ? title
      : withTagline.length <= 60
        ? withTagline
        : withBrand.length <= 60
          ? withBrand
          : title;
    document.title = fullTitle;

    const pagePath = path ?? window.location.pathname;
    const canonical = `${SITE_URL}${pagePath === "/" ? "" : pagePath}`;
    const absImage = image.startsWith("http") ? image : `${SITE_URL}${image}`;

    setTag('meta[name="description"]', { name: "description", content: description });
    setTag('link[rel="canonical"]', { rel: "canonical", href: canonical }, "link");
    setTag('meta[name="robots"]', {
      name: "robots",
      content: noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    });
    if (keywords) {
      setTag('meta[name="keywords"]', { name: "keywords", content: keywords });
    }

    // Open Graph
    setTag('meta[property="og:type"]', { property: "og:type", content: type });
    setTag('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    setTag('meta[property="og:description"]', { property: "og:description", content: description });
    setTag('meta[property="og:url"]', { property: "og:url", content: canonical });
    setTag('meta[property="og:image"]', { property: "og:image", content: absImage });
    setTag('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    setTag('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
    setTag('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    setTag('meta[property="og:locale"]', { property: "og:locale", content: "en_US" });

    // Twitter
    setTag('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setTag('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    setTag('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setTag('meta[name="twitter:image"]', { name: "twitter:image", content: absImage });

    // JSON-LD
    document.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove());
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.dataset.seoJsonld = "true";
        s.textContent = JSON.stringify(block);
        document.head.appendChild(s);
      });
    }
  }, [title, description, path, image, type, noBrandSuffix, noindex, keywords, jsonLd]);

  return null;
}

export const SITE = { url: SITE_URL, name: SITE_NAME, tagline: TAGLINE, image: DEFAULT_IMAGE };
